import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import NotificationBell from '@/components/NotificationBell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import {
  Home,
  Calendar,
  Users,
  Briefcase,
  FileText,
  Settings,
  LogOut,
  Menu,
  Sun,
  Moon,
  ChevronRight,
  Clock,
  User,
  Tag,
  Globe,
  DollarSign,
  Shield,
  CreditCard,
  Plug,
  TrendingUp,
  Receipt,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Navigation items based on role
const getNavItems = (role, t) => {
  const items = {
    worker: [
      { icon: Home, label: t('nav.myDay'), href: '/dashboard', testId: 'nav-dashboard' },
      { icon: Calendar, label: t('nav.calendar'), href: '/calendar', testId: 'nav-calendar' },
      { icon: Clock, label: t('nav.time'), href: '/time', testId: 'nav-time' },
    ],
    manager: [
      { icon: Home, label: t('nav.overview'), href: '/dashboard', testId: 'nav-dashboard' },
      { icon: Calendar, label: t('nav.calendar'), href: '/calendar', testId: 'nav-calendar' },
      { icon: TrendingUp, label: 'Team Load', href: '/scheduling', testId: 'nav-scheduling' },
      { icon: Briefcase, label: t('nav.orders'), href: '/orders', testId: 'nav-orders' },
      { icon: Users, label: t('nav.clients'), href: '/clients', testId: 'nav-clients' },
      { icon: User, label: t('nav.workers'), href: '/workers', testId: 'nav-workers' },
      { icon: Tag, label: t('nav.services'), href: '/services', testId: 'nav-services' },
      { icon: FileText, label: t('nav.reports'), href: '/reports', testId: 'nav-reports' },
      { icon: Clock, label: t('nav.time'), href: '/time', testId: 'nav-time' },
      { icon: DollarSign, label: t('payroll.title'), href: '/payroll', testId: 'nav-payroll' },
      { icon: CreditCard, label: t('nav.payments'), href: '/payments', testId: 'nav-payments' },
      { icon: Receipt, label: t('nav.expenses') || 'Expenses', href: '/expenses', testId: 'nav-expenses' },
      { icon: Shield, label: t('nav.audit'), href: '/audit', testId: 'nav-audit' },
    ],
    admin: [
      { icon: Home, label: t('nav.overview'), href: '/dashboard', testId: 'nav-dashboard' },
      { icon: Calendar, label: t('nav.calendar'), href: '/calendar', testId: 'nav-calendar' },
      { icon: TrendingUp, label: 'Team Load', href: '/scheduling', testId: 'nav-scheduling' },
      { icon: Briefcase, label: t('nav.orders'), href: '/orders', testId: 'nav-orders' },
      { icon: Users, label: t('nav.clients'), href: '/clients', testId: 'nav-clients' },
      { icon: User, label: t('nav.workers'), href: '/workers', testId: 'nav-workers' },
      { icon: Tag, label: t('nav.services'), href: '/services', testId: 'nav-services' },
      { icon: FileText, label: t('nav.reports'), href: '/reports', testId: 'nav-reports' },
      { icon: Clock, label: t('nav.time'), href: '/time', testId: 'nav-time' },
      { icon: DollarSign, label: t('payroll.title'), href: '/payroll', testId: 'nav-payroll' },
      { icon: CreditCard, label: t('nav.payments'), href: '/payments', testId: 'nav-payments' },
      { icon: Receipt, label: t('nav.expenses') || 'Expenses', href: '/expenses', testId: 'nav-expenses' },
      { icon: Shield, label: t('nav.audit'), href: '/audit', testId: 'nav-audit' },
      { icon: Plug, label: t('nav.integrations'), href: '/integrations', testId: 'nav-integrations' },
      { icon: Settings, label: t('nav.settings'), href: '/settings', testId: 'nav-settings' },
    ],
  };
  return items[role] || items.worker;
};

// Desktop Sidebar
function Sidebar({ navItems, currentPath }) {
  return (
    <aside className="hidden md:flex cgc-sidebar" data-testid="sidebar">
      <div className="flex items-center gap-3 mb-8 px-2">
        <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
          <Briefcase className="h-5 w-5 text-white" />
        </div>
        <span className="text-xl font-bold">CGC-CRM</span>
      </div>
      <nav className="flex-1 space-y-1">
        {navItems.map((item) => {
          const isActive = currentPath === item.href;
          return (
            <Link
              key={item.href}
              to={item.href}
              data-testid={item.testId}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                isActive
                  ? 'bg-white/10 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              )}
            >
              <item.icon className="h-5 w-5" />
              <span className="font-medium">{item.label}</span>
              {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
            </Link>
          );
        })}
      </nav>
      <div className="mt-auto pt-4 border-t border-white/10 text-xs text-slate-500 px-2">
        CGC-CRM v1.15
      </div>
    </aside>
  );
}

// Mobile Bottom Navigation
function BottomNav({ navItems, currentPath }) {
  const mobileItems = navItems.slice(0, 5);
  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-card/95 backdrop-blur-lg border-t border-border z-50 safe-area-pb" data-testid="bottom-nav">
      <div className="flex items-center justify-around h-16">
        {mobileItems.map((item) => {
          const isActive = currentPath === item.href;
          return (
            <Link
              key={item.href}
              to={item.href}
              data-testid={`mobile-${item.testId}`}
              className={cn(
                'flex flex-col items-center justify-center gap-1 px-3 py-2 min-w-[64px] transition-colors',
                isActive ? 'text-accent' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <item.icon className={cn('h-5 w-5 transition-transform', isActive && 'scale-110')} />
              <span className="text-[10px] font-medium truncate max-w-[60px]">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

// Header for mobile — BURGER FIX: controlled open state
function MobileHeader({ user, onLogout }) {
  const { theme, toggleTheme } = useTheme();
  const { t, lang, toggleLanguage } = useLanguage();
  const navItems = getNavItems(user?.role, t);
  const [menuOpen, setMenuOpen] = useState(false);
  
  return (
    <header className="md:hidden sticky top-0 z-30 glass border-b border-border px-4 py-3 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-menu">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 bg-slate-900 text-white p-0">
            <SheetTitle className="sr-only">{t('common.navigation')}</SheetTitle>
            <SheetDescription className="sr-only">{t('common.mainMenu')}</SheetDescription>
            <div className="p-4">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
                  <Briefcase className="h-5 w-5" />
                </div>
                <span className="text-xl font-bold">CGC-CRM</span>
              </div>
              <nav className="space-y-1">
                {navItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5"
                  >
                    <item.icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </Link>
                ))}
              </nav>
            </div>
          </SheetContent>
        </Sheet>
        <span className="font-bold text-lg">CGC-CRM</span>
      </div>

      <div className="flex items-center gap-1">
        <NotificationBell />
        <Button variant="ghost" size="icon" onClick={toggleLanguage} data-testid="lang-toggle" title={t('common.language')}>
          <span className="text-xs font-bold">{lang === 'ru' ? 'EN' : 'RU'}</span>
        </Button>
        <Button variant="ghost" size="icon" onClick={toggleTheme}>
          {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
        </Button>
        <UserMenu user={user} onLogout={onLogout} />
      </div>
    </header>
  );
}

// User dropdown menu
function UserMenu({ user, onLogout }) {
  const { t } = useLanguage();
  const initials = user 
    ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase()
    : 'U';
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-accent text-white text-sm">
              {initials}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span className="font-medium">{user?.first_name} {user?.last_name}</span>
            <span className="text-xs text-muted-foreground">{user?.email}</span>
            <span className="text-xs text-accent mt-1">{t(`role.${user?.role}`)}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/profile" className="cursor-pointer">
            <User className="mr-2 h-4 w-4" />
            {t('common.profile')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/settings" className="cursor-pointer">
            <Settings className="mr-2 h-4 w-4" />
            {t('common.settings')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem 
          onClick={onLogout}
          className="text-destructive cursor-pointer"
          data-testid="logout-btn"
        >
          <LogOut className="mr-2 h-4 w-4" />
          {t('common.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Desktop Header with language toggle
function DesktopHeader({ user, onLogout }) {
  const { theme, toggleTheme } = useTheme();
  const { lang, toggleLanguage, t } = useLanguage();
  
  return (
    <header className="hidden md:flex sticky top-0 z-30 glass border-b border-border px-6 py-3 items-center justify-between">
      <div />
      <div className="flex items-center gap-3">
        <NotificationBell />
        <Button variant="ghost" size="sm" onClick={toggleLanguage} data-testid="desktop-lang-toggle" title={t('common.language')}>
          <Globe className="h-4 w-4 mr-1.5" />
          <span className="text-xs font-medium">{lang === 'ru' ? 'EN' : 'RU'}</span>
        </Button>
        <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="desktop-theme-toggle">
          {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
        </Button>
        <UserMenu user={user} onLogout={onLogout} />
      </div>
    </header>
  );
}

// Main Layout Component
export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  
  const navItems = getNavItems(user?.role, t);
  const currentPath = location.pathname;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors">
      <Sidebar navItems={navItems} currentPath={currentPath} />
      <div className="md:ml-64 pb-24 md:pb-0 min-h-screen bg-muted/30">
        <MobileHeader user={user} onLogout={handleLogout} />
        <DesktopHeader user={user} onLogout={handleLogout} />
        <main className="p-4 md:p-6 animate-fade-in">
          {children}
        </main>
      </div>
      <BottomNav navItems={navItems} currentPath={currentPath} />
    </div>
  );
}
